import { get, cloneDeep, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import GetRecords from '../../../../../../../lib/api/GetRecords';
import { APIS } from '../../../../../../../lib/constants/apis';
import {
	removeNullDeep,
	removeDataKeys,
	getKostenKategorieArt,
	getOptionsFromRes,
	addIndexesToPath,
} from '../../../../../../../utils/helper';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import AddIcon from '@mui/icons-material/Add';
import Schema from '../../../../../../../lib/paths/verwendungsnachweis_FULL_Paths.json';
import { Button } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from '../../../../../../../components/ui-component/mui-accordion/MUIAccordionComponent';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import { getFormStrucComponentTable } from '../../../../../../../utils/formStruc';
import { concat } from 'lodash';
const P = Schema.attributes;

const PersonalKostenVN: FC<{ kostentraeger: string; unternehmenId?: number }> = ({ kostentraeger, unternehmenId }) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [kostenKategorie, setKostenKategorie] = useState([]);

	const handleAddPersonalKosten = () => {
		try {
			let appCopy = cloneDeep(application);
			const data = {
				Kostentraeger: kostentraeger,
				Organisation: { id: unternehmenId },
				Ausgabenkategorie: kostenKategorie[0],
				Ausgaben_Ausgaben_Block_1: [{ Zelle_1: '' }],
			};
			set(appCopy, `${P.Ausgaben.path}.${get(application, P.Ausgaben.path, []).length}`, data);
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Adding Personalkosten ', err);
		}
	};

	const handleDeletePersonalKosten = async (index: number) => {
		try {
			let appCopy = cloneDeep(application);
			const allKostenPosition = get(appCopy, P.Ausgaben.path, []);
			set(
				appCopy,
				P.Ausgaben.path,
				allKostenPosition.filter((_: any, i: number) => i !== index),
			);
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten ', err);
		}
	};

	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = {
					Allgemein_Kostenposition: 'Ausgabe',
					Allgemein_Kostenart_Finanzierung: 'Personalkosten',
					Allgemein_Foerderprogramm: {
						$contains: get(application, P.Zuwendungsantrag.schema.attributes.Foerderprogramm.path + '.id'),
					},
				};
				let res = await GetRecords(APIS.KostenKategories, [], filter);

				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategories', err);
			}
		};
		fetch();
	}, []);

	return (
		<>
			{get(application, P.Ausgaben.path, []).map((ele: any, i: number) => {
				if (
					get(ele, 'Organisation.id') === unternehmenId &&
					get(ele, 'Kostentraeger') === kostentraeger &&
					getKostenKategorieArt(get(ele, 'Ausgabenkategorie'), kostenKategorie) === 'Personalkosten'
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle="Personalkosten"
							onDelete={() => handleDeletePersonalKosten(i)}
							showDeleteButton={!readOnly}
						>
							<Grid2 container spacing={6}>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapPersonalKosten(strucPersonalKosten(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={readOnly}
									/>
								</Grid2>
							</Grid2>
							<CreateForm
								formStruc={mapPersonalKosten(formStrucErlaeuterungTable(application, i), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={readOnly}
							/>
						</MUIAccordionComponent>
					);
				}
			})}

			<Button sx={{ mt: '2rem' }} endIcon={<AddIcon />} onClick={handleAddPersonalKosten} disabled={readOnly}>
				Personalkosten hinzufügen
			</Button>
		</>
	);
};
export default PersonalKostenVN;

const mapPersonalKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Ausgaben.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Ausgaben.path.length),
				};
			}),
		};
	});
	return result;
};

const strucPersonalKosten = (kostenKategorie: any) => {
	return [
		{
			fields: [
				{
					...P.Ausgaben.schema.attributes.Ausgabenkategorie,
					type: 'enumeration',
					options: getOptionsFromRes(kostenKategorie, 'Allgemein_Bezeichnung'),
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};

const formStrucErlaeuterungTable = (application: any, i: number) => {
	return [
		{
			fields: [
				{
					...P.Ausgaben.schema.attributes.Ausgaben_Ausgaben_Block_1,
					type: 'componentTable',
					hideColumns: [
						'Zelle_4',
						...(get(application, P.Verwendungsnachweisstatus_Status.path) === 'inBearbeitung'
							? ['Zelle_3', 'Zelle_5']
							: []),
					],
					disabledColumns: ['Zelle_4', 'Zelle_3', 'Zelle_5'],
					fieldProps: { Zelle_2: { format: 'currency', label: 'Verausgabt (€)' }, Zelle_3: { format: 'currency' } },
					sumRow: [
						{
							id: 'pinnedRow',
							Zelle_1: 'Zwischensumme',
							Zelle_2: get(
								application,
								addIndexesToPath(P.Ausgaben.schema.attributes.Zelle_2.path, [`${i}`], ['Ausgaben']),
							),
							Zelle_3: get(
								application,
								addIndexesToPath(P.Ausgaben.schema.attributes.Zelle_3.path, [`${i}`], ['Ausgaben']),
							),
						},
					],
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};

export const GetVNPersonalKostenFormStruc = (
	application: any,
	unternehmenId: number,
	kostentraeger: string,
	kostenKategorie: any,
) => {
	let formStruc: FormStrucItem[] = [];
	get(application, P.Ausgaben.path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, 'Organisation.id') === unternehmenId &&
			get(ele, 'Kostentraeger') === kostentraeger &&
			getKostenKategorieArt(get(ele, 'Ausgabenkategorie'), kostenKategorie) === 'Personalkosten'
		) {
			const formStrucCT2WithIndex = getFormStrucComponentTable(
				application,
				mapPersonalKosten(formStrucErlaeuterungTable(application, i), i),
			);
			const formStruc1WithIndex = mapPersonalKosten(strucPersonalKosten([]), i);
			formStruc = concat(formStruc, formStruc1WithIndex, formStrucCT2WithIndex);
		}
	});
	return formStruc;
};
